import PageHeader from "@/components/common/PageHeader";
import {
  importSupportService,
  type ImportSupportJob,
} from "@/services/import-support.service";
import { tenantService } from "@/services/tenant.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  FileSearch,
  RefreshCw,
  ShieldCheck,
  Square,
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";

const JOB_STATUSES = [
  "UPLOADED",
  "MAPPED",
  "VALIDATED",
  "REVIEW_REQUIRED",
  "APPROVED",
  "COMMITTED",
  "FAILED",
  "UNDONE",
];

function messageOf(error: unknown) {
  return error instanceof Error ? error.message : "The request failed";
}

function formatDate(value?: string | null) {
  return value ? new Date(value).toLocaleString() : "—";
}

function statusClass(status: string) {
  if (["COMMITTED", "COMPLETED", "VALIDATED"].includes(status)) {
    return "bg-emerald-100 text-emerald-700";
  }
  if (["FAILED", "REVIEW_REQUIRED"].includes(status)) {
    return "bg-red-100 text-red-700";
  }
  return "bg-amber-100 text-amber-700";
}

function remainingText(expiresAt?: string) {
  if (!expiresAt) return "";
  const remaining = Math.max(0, new Date(expiresAt).getTime() - Date.now());
  const minutes = Math.floor(remaining / 60_000);
  const seconds = Math.floor((remaining % 60_000) / 1_000);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function JobRow({
  job,
  selected,
  onSelect,
}: {
  job: ImportSupportJob;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`w-full rounded-xl border p-4 text-left transition ${
        selected
          ? "border-slate-900 bg-slate-50"
          : "border-slate-200 bg-white hover:border-slate-400"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-slate-900">
            {job.file.originalFilename}
          </p>
          <p className="mt-1 font-mono text-[11px] text-slate-400">{job.id}</p>
        </div>
        <span
          className={`rounded-full px-2 py-1 text-[10px] font-bold ${statusClass(job.status)}`}
        >
          {job.status}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-slate-500">
        <span>{job._count.rows} rows</span>
        <span>{job._count.errors} errors</span>
        <span>{formatDate(job.updatedAt)}</span>
      </div>
    </button>
  );
}

function ImportSupportPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const accessId = searchParams.get("access") ?? "";
  const selectedJobId = searchParams.get("job") ?? "";
  const [tenantId, setTenantId] = useState("");
  const [storeId, setStoreId] = useState("");
  const [reason, setReason] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(20);
  const [status, setStatus] = useState(searchParams.get("status") ?? "");
  const [jobIdFilter, setJobIdFilter] = useState("");
  const [retryReason, setRetryReason] = useState("");
  const [, setTick] = useState(0);

  const tenantsQuery = useQuery({
    queryKey: ["import-support-tenants"],
    queryFn: () => tenantService.list({ page: 1, limit: 100 }),
  });
  const tenantQuery = useQuery({
    queryKey: ["import-support-tenant", tenantId],
    queryFn: () => tenantService.getById(tenantId),
    enabled: Boolean(tenantId),
  });
  const accessQuery = useQuery({
    queryKey: ["import-support-access", accessId],
    queryFn: () => importSupportService.getAccess(accessId),
    enabled: Boolean(accessId),
    retry: false,
  });
  const jobsQuery = useQuery({
    queryKey: ["import-support-jobs", accessId, status, jobIdFilter],
    queryFn: () =>
      importSupportService.listJobs(accessId, {
        status: status || undefined,
        jobId: jobIdFilter.trim() || undefined,
      }),
    enabled: Boolean(accessId) && accessQuery.isSuccess,
    retry: false,
  });
  const detailsQuery = useQuery({
    queryKey: ["import-support-job", accessId, selectedJobId],
    queryFn: () => importSupportService.getJob(accessId, selectedJobId),
    enabled:
      Boolean(accessId) && Boolean(selectedJobId) && accessQuery.isSuccess,
    retry: false,
  });

  useEffect(() => {
    if (!accessQuery.data?.expiresAt) return;
    const interval = window.setInterval(
      () => setTick((value) => value + 1),
      1_000,
    );
    return () => window.clearInterval(interval);
  }, [accessQuery.data?.expiresAt]);

  const sessionExpired = useMemo(
    () =>
      Boolean(accessQuery.data) &&
      (Boolean(accessQuery.data?.endedAt) ||
        new Date(accessQuery.data!.expiresAt).getTime() <= Date.now()),
    [accessQuery.data],
  );

  const updateParams = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    setSearchParams(next, { replace: true });
  };

  const createMutation = useMutation({
    mutationFn: () =>
      importSupportService.createAccess({
        tenantId,
        storeId,
        reason: reason.trim(),
        durationMinutes,
      }),
    onSuccess: (access) => {
      updateParams({ access: access.id, job: null, status: null });
      setStatus("");
      toast.success("Controlled support access started");
    },
    onError: (error) => toast.error(messageOf(error)),
  });

  const endMutation = useMutation({
    mutationFn: () => importSupportService.endAccess(accessId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["import-support-access", accessId],
      });
      toast.success("Support access ended");
    },
    onError: (error) => toast.error(messageOf(error)),
  });

  const retryMutation = useMutation({
    mutationFn: () => {
      const storageKey = `import-support-retry:${accessId}:${selectedJobId}:${retryReason.trim()}`;
      let idempotencyKey = sessionStorage.getItem(storageKey);
      if (!idempotencyKey) {
        idempotencyKey = crypto.randomUUID();
        sessionStorage.setItem(storageKey, idempotencyKey);
      }
      return importSupportService.retry(
        accessId,
        selectedJobId,
        retryReason.trim(),
        idempotencyKey,
      );
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["import-support-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["import-support-job"] });
      toast.success(
        result.replayed
          ? "The recorded retry result was replayed"
          : "Validation retry completed",
      );
    },
    onError: (error) => toast.error(messageOf(error)),
  });

  const onCreate = (event: FormEvent) => {
    event.preventDefault();
    if (!tenantId || !storeId || reason.trim().length < 10) {
      toast.error("Choose a tenant/store and provide a specific reason");
      return;
    }
    createMutation.mutate();
  };

  if (!accessId) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Import support"
          subtitle="Start a time-bound, audited session before inspecting tenant import data."
        />
        <form
          onSubmit={onCreate}
          className="max-w-3xl space-y-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <div className="flex items-start gap-3 rounded-xl bg-blue-50 p-4 text-sm text-blue-800">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
            <p>
              Access is limited to one store, expires automatically, and records
              every view and action. Tenant impersonation is not used.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2 text-sm font-semibold text-slate-700">
              Tenant
              <select
                required
                value={tenantId}
                onChange={(event) => {
                  setTenantId(event.target.value);
                  setStoreId("");
                }}
                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 font-normal"
              >
                <option value="">Choose tenant</option>
                {tenantsQuery.data?.data.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name} · {tenant.email}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-2 text-sm font-semibold text-slate-700">
              Store
              <select
                required
                value={storeId}
                onChange={(event) => setStoreId(event.target.value)}
                disabled={!tenantId || tenantQuery.isLoading}
                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 font-normal disabled:bg-slate-50"
              >
                <option value="">Choose store</option>
                {tenantQuery.data?.stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name} · {store.city}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="block space-y-2 text-sm font-semibold text-slate-700">
            Diagnostic reason
            <textarea
              required
              minLength={10}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Ticket number and the import symptom being investigated"
              className="min-h-24 w-full rounded-xl border border-slate-200 px-3 py-2 font-normal"
            />
          </label>
          <label className="block space-y-2 text-sm font-semibold text-slate-700">
            Duration
            <select
              value={durationMinutes}
              onChange={(event) =>
                setDurationMinutes(Number(event.target.value))
              }
              className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 font-normal"
            >
              {[5, 10, 20, 30, 45, 60].map((value) => (
                <option key={value} value={value}>
                  {value} minutes
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="rounded-xl bg-slate-900 px-5 py-3 text-sm font-bold text-white disabled:opacity-50"
          >
            {createMutation.isPending ? "Starting…" : "Start support access"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Import support"
        subtitle="Store-scoped diagnostics with audited, retry-safe actions."
        actions={
          <button
            type="button"
            disabled={sessionExpired || endMutation.isPending}
            onClick={() => endMutation.mutate()}
            className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-white px-4 py-2 text-sm font-bold text-red-700 disabled:opacity-50"
          >
            <Square className="h-4 w-4" /> End access
          </button>
        }
      />

      {accessQuery.isLoading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
          Verifying controlled access…
        </div>
      ) : accessQuery.isError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
          Access cannot be resumed: {messageOf(accessQuery.error)}
        </div>
      ) : (
        <div
          className={`flex flex-wrap items-center justify-between gap-4 rounded-2xl border p-4 ${
            sessionExpired
              ? "border-red-200 bg-red-50"
              : "border-blue-200 bg-blue-50"
          }`}
        >
          <div className="flex items-center gap-3">
            {sessionExpired ? (
              <AlertTriangle className="h-5 w-5 text-red-600" />
            ) : (
              <ShieldCheck className="h-5 w-5 text-blue-700" />
            )}
            <div>
              <p className="text-sm font-bold text-slate-900">
                {sessionExpired ? "Access ended" : "Controlled access active"}
              </p>
              <p className="text-xs text-slate-600">
                {accessQuery.data?.reason} · store {accessQuery.data?.storeId}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 font-mono text-sm font-bold text-slate-800">
            <Clock3 className="h-4 w-4" />
            {sessionExpired
              ? "00:00"
              : remainingText(accessQuery.data?.expiresAt)}
          </div>
        </div>
      )}

      {!sessionExpired && accessQuery.isSuccess && (
        <>
          <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 md:grid-cols-[180px_1fr_auto]">
            <select
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                updateParams({ status: event.target.value || null, job: null });
              }}
              className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm"
            >
              <option value="">All statuses</option>
              {JOB_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <input
              value={jobIdFilter}
              onChange={(event) => setJobIdFilter(event.target.value)}
              placeholder="Exact job UUID (optional)"
              className="h-10 rounded-xl border border-slate-200 px-3 text-sm"
            />
            <button
              type="button"
              onClick={() => jobsQuery.refetch()}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 text-sm font-bold"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
          </div>

          <div className="grid gap-6 xl:grid-cols-[360px_1fr]">
            <section className="space-y-3">
              <h3 className="text-sm font-black uppercase tracking-widest text-slate-500">
                Import jobs
              </h3>
              {jobsQuery.isLoading ? (
                <p className="text-sm text-slate-500">Loading import health…</p>
              ) : jobsQuery.data?.jobs.length ? (
                jobsQuery.data.jobs.map((job) => (
                  <JobRow
                    key={job.id}
                    job={job}
                    selected={job.id === selectedJobId}
                    onSelect={() => updateParams({ job: job.id })}
                  />
                ))
              ) : (
                <div className="rounded-xl border border-dashed border-slate-300 p-6 text-sm text-slate-500">
                  No imports match this store and filter.
                </div>
              )}
            </section>

            <section className="min-w-0 space-y-5">
              {!selectedJobId ? (
                <div className="flex min-h-72 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white text-center">
                  <FileSearch className="mb-3 h-8 w-8 text-slate-400" />
                  <p className="text-sm font-bold text-slate-700">
                    Select an import to inspect its evidence
                  </p>
                </div>
              ) : detailsQuery.isLoading ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
                  Loading scoped diagnostics…
                </div>
              ) : detailsQuery.isError ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
                  {messageOf(detailsQuery.error)}
                </div>
              ) : detailsQuery.data ? (
                <>
                  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <p className="text-lg font-black text-slate-900">
                          {detailsQuery.data.job.file.originalFilename}
                        </p>
                        <p className="mt-1 font-mono text-xs text-slate-400">
                          SHA-256 {detailsQuery.data.job.file.checksumSha256}
                        </p>
                      </div>
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-bold ${statusClass(detailsQuery.data.job.status)}`}
                      >
                        {detailsQuery.data.job.status}
                      </span>
                    </div>
                    <div className="mt-5 grid gap-3 sm:grid-cols-4">
                      {[
                        ["Rows", detailsQuery.data.job.rowCount],
                        ["Accepted", detailsQuery.data.job.acceptedRows],
                        ["Rejected", detailsQuery.data.job.rejectedRows],
                        ["Errors", detailsQuery.data.job.errors.length],
                      ].map(([label, value]) => (
                        <div key={label} className="rounded-xl bg-slate-50 p-3">
                          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                            {label}
                          </p>
                          <p className="mt-1 text-xl font-black text-slate-900">
                            {value}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {detailsQuery.data.job.status === "FAILED" && (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                      <h3 className="flex items-center gap-2 text-sm font-black text-amber-900">
                        <RefreshCw className="h-4 w-4" /> Retry validation
                      </h3>
                      <p className="mt-1 text-xs text-amber-800">
                        This invokes the normal scoped import service. Repeating
                        the same request replays its recorded result.
                      </p>
                      <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                        <input
                          value={retryReason}
                          onChange={(event) =>
                            setRetryReason(event.target.value)
                          }
                          placeholder="Why this retry is safe and necessary"
                          className="h-10 flex-1 rounded-xl border border-amber-200 bg-white px-3 text-sm"
                        />
                        <button
                          type="button"
                          disabled={
                            retryReason.trim().length < 5 ||
                            retryMutation.isPending
                          }
                          onClick={() => retryMutation.mutate()}
                          className="rounded-xl bg-amber-900 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
                        >
                          {retryMutation.isPending
                            ? "Retrying…"
                            : "Retry safely"}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <h3 className="text-sm font-black text-slate-900">
                      Errors
                    </h3>
                    <div className="mt-3 max-h-72 space-y-2 overflow-y-auto">
                      {detailsQuery.data.job.errors.length ? (
                        detailsQuery.data.job.errors.map((error) => (
                          <div
                            key={error.id}
                            className="rounded-xl border border-red-100 bg-red-50 p-3"
                          >
                            <div className="flex justify-between gap-3">
                              <p className="text-xs font-bold text-red-800">
                                {error.code} · {error.field ?? "row"}
                              </p>
                              <span className="text-[10px] text-red-500">
                                row {error.row?.sourceRow ?? "—"}
                              </span>
                            </div>
                            <p className="mt-1 text-sm text-red-700">
                              {error.message}
                            </p>
                          </div>
                        ))
                      ) : (
                        <p className="flex items-center gap-2 text-sm text-emerald-700">
                          <CheckCircle2 className="h-4 w-4" /> No recorded
                          errors
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-5 lg:grid-cols-2">
                    <EvidenceList
                      title="Import audit"
                      items={detailsQuery.data.audit.map((event) => ({
                        id: event.id,
                        label: event.action,
                        detail: event.actorId ?? "system",
                        date: event.createdAt,
                      }))}
                    />
                    <EvidenceList
                      title="Outbox delivery"
                      items={detailsQuery.data.outbox.map((event) => ({
                        id: event.id,
                        label: `${event.eventType} · ${event.status}`,
                        detail:
                          event.lastError ?? `${event.attempts} attempt(s)`,
                        date: event.createdAt,
                      }))}
                    />
                    <EvidenceList
                      title="Support access audit"
                      items={detailsQuery.data.supportActions.map((action) => ({
                        id: action.id,
                        label: `${action.action} · ${action.status}`,
                        detail: action.error ?? "Recorded",
                        date: action.createdAt,
                      }))}
                    />
                    <div className="rounded-2xl border border-slate-200 bg-white p-5">
                      <h3 className="text-sm font-black text-slate-900">
                        Reconciliation
                      </h3>
                      <pre className="mt-3 max-h-56 overflow-auto whitespace-pre-wrap rounded-xl bg-slate-950 p-3 text-[11px] text-slate-200">
                        {JSON.stringify(
                          detailsQuery.data.job.reconciliation ?? {
                            state: "Not available",
                          },
                          null,
                          2,
                        )}
                      </pre>
                    </div>
                  </div>
                </>
              ) : null}
            </section>
          </div>
        </>
      )}
    </div>
  );
}

function EvidenceList({
  title,
  items,
}: {
  title: string;
  items: Array<{ id: string; label: string; detail: string; date: string }>;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <h3 className="text-sm font-black text-slate-900">{title}</h3>
      <div className="mt-3 max-h-56 space-y-2 overflow-y-auto">
        {items.length ? (
          items.map((item) => (
            <div key={item.id} className="rounded-xl bg-slate-50 p-3">
              <p className="text-xs font-bold text-slate-800">{item.label}</p>
              <p className="mt-1 break-words text-xs text-slate-500">
                {item.detail}
              </p>
              <p className="mt-1 text-[10px] text-slate-400">
                {formatDate(item.date)}
              </p>
            </div>
          ))
        ) : (
          <p className="text-sm text-slate-500">No records.</p>
        )}
      </div>
    </div>
  );
}

export default ImportSupportPage;
